"use client";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

interface AdSlotProps {
  slot: string;
  format?: string;
  layout?: string;
  layoutKey?: string;
  responsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

export function AdSlot({
  slot,
  format = "auto",
  layout,
  layoutKey,
  responsive = true,
  className,
  style,
}: AdSlotProps) {
  const pushedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const insRef = useRef<HTMLModElement | null>(null);
  const [unfilled, setUnfilled] = useState(false);

  // Watch the <ins> for AdSense's data-ad-status="unfilled" signal and
  // collapse the wrapper when it appears, so we don't show an empty card
  // sitting in the layout. AdSense sets this attribute synchronously after
  // it decides not to fill the slot.
  useEffect(() => {
    const ins = insRef.current;
    if (!ins) return;
    const check = () => {
      if (ins.getAttribute("data-ad-status") === "unfilled") {
        setUnfilled((prev) => {
          if (!prev) pushEvent(analyticsEvents.ad_slot_unfilled, { slot });
          return true;
        });
      }
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
    return () => obs.disconnect();
  }, [slot]);

  // Lazy-push: only fire adsbygoogle.push({}) once the slot is within ~400px
  // of the viewport. Below-the-fold slots no longer waste impressions, which
  // raises viewability and the CPM tier AdSense assigns to the page.
  // Each slot still pushes exactly once (pushedRef guard); intersections
  // fire top-to-bottom which matches DOM order, so AdSense queue ordering
  // stays correct.
  //
  // Observe the wrapper, not the <ins>: the bare <ins> collapses to 0px tall
  // before AdSense fills it, and 0-height targets don't reliably fire
  // isIntersecting=true across browsers. The wrapper carries min-h-25 so it
  // always has measurable area.
  useEffect(() => {
    if (pushedRef.current) return;
    const node = wrapperRef.current;
    if (!node) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Fluid responsive ads need >=250px or AdSense rejects the fill with
    // "availableWidth=0" — re-check until the wrapper has a usable width
    // (covers mid-layout pushes, briefly-hidden parents) instead of burning
    // the push on a zero-width slot.
    const push = (retries = 0) => {
      if (pushedRef.current) return;
      const w = wrapperRef.current?.offsetWidth ?? 0;
      if (w < 250) {
        if (retries < 10) timer = setTimeout(() => push(retries + 1), 500);
        return;
      }
      try {
        (window.adsbygoogle = window.adsbygoogle ?? []).push({});
        pushedRef.current = true;
      } catch {
        // adsbygoogle.push throws if the loader hasn't initialised; ignore.
      }
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            pushEvent(analyticsEvents.ad_slot_view, { slot });
            // Match the Angular site's 2s settle delay before push({}).
            // Pushing immediately on intersect was leaving slots in
            // status="unfilled" — adsbygoogle.js seems to need a moment
            // after script-load to settle Auto Ads scanning before manual
            // slots get a fair fill response.
            timer = setTimeout(() => push(), 2000);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(node);
    return () => {
      io.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [slot]);

  const client = process.env.NEXT_PUBLIC_ADSENSE_CLIENT;
  if (!client || !slot) return null;

  // Dev placeholder: AdSense returns 400 for unregistered hosts (e.g.
  // local subdomains) even with adtest=on, so render a labeled box that
  // matches the real ad's footprint — lets us verify layout without
  // depending on AdSense approval. Bypass with NEXT_PUBLIC_ADSENSE_LIVE_DEV=true.
  const showPlaceholder =
    process.env.NODE_ENV !== "production" && process.env.NEXT_PUBLIC_ADSENSE_LIVE_DEV !== "true";

  if (showPlaceholder) {
    return (
      <div
        className={cn(
          "border-border bg-muted/40 text-muted-foreground flex min-h-25 items-center justify-center rounded-md border border-dashed text-[11px] font-medium tracking-wide uppercase",
          className,
        )}
        style={style}
        aria-label="Ad placeholder"
      >
        Ad placeholder · slot {slot}
      </div>
    );
  }

  const testMode =
    process.env.NEXT_PUBLIC_ADSENSE_TEST === "true" || process.env.NODE_ENV !== "production";

  // Fluid native ads size themselves via the layout key — combining
  // data-full-width-responsive with format="fluid" makes AdSense reject the
  // slot. Only emit the responsive flag for non-fluid formats.
  const wantsResponsive = responsive && format !== "fluid";

  // Keep the <ins> itself bare — only `adsbygoogle` + display:block. AdSense's
  // fluid-native renderer creates `aswift_N_host` inside the <ins> and computes
  // layout from the host element, so background, border-radius, shadow, or
  // min-height applied directly to the <ins> can cause Google to return
  // status="unfilled". All visual styling lives on the wrapper instead.
  return (
    <div
      ref={wrapperRef}
      data-track-zone="ad-slot"
      data-ad-slot-id={slot}
      data-track-view={`ad_slot_${slot}`}
      className={cn(
        // Plain block, no flex: AdSense's fluid-native renderer measures
        // width off the wrapper, and wrapping the <ins> as a flex item with
        // width:100% + justify-center is the recommended-against pattern
        // that occasionally measures as availableWidth=0. min-h-30 (~120px)
        // reserves roughly the fluid-native fill height up front so a
        // successful fill doesn't shove the page content down.
        "min-h-30 w-full",
        className,
      )}
      // When AdSense returns no ad (data-ad-status="unfilled"), collapse the
      // slot with display:none so the page closes the gap instead of leaving
      // a blank reserved box. The lazy-load observer has already fired by the
      // time a status is known, so collapsing here is safe — a deliberate
      // trade of a small layout shift for never showing dead space.
      style={unfilled ? { ...style, display: "none" } : style}
    >
      <ins
        ref={insRef}
        className="adsbygoogle w-full"
        style={{ display: "block" }}
        data-ad-client={client}
        data-ad-slot={slot}
        data-ad-format={format}
        {...(layout ? { "data-ad-layout": layout } : {})}
        {...(layoutKey ? { "data-ad-layout-key": layoutKey } : {})}
        {...(wantsResponsive ? { "data-full-width-responsive": "true" } : {})}
        {...(testMode ? { "data-adtest": "on" } : {})}
      />
    </div>
  );
}
